<script setup lang="ts">
import type { Locale, RadioFieldConfig } from '@/forms/types'

const props = defineProps<{
  field: RadioFieldConfig
  locale: Locale
  modelValue: string
  error?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const errorId = `${props.field.id}-error`
</script>

<template>
  <fieldset
    class="field"
    :class="{ 'has-error': !!error }"
    :aria-invalid="error ? 'true' : undefined"
    :aria-describedby="error ? errorId : undefined"
  >
    <legend class="field-label">
      {{ field.label[locale] }}
      <span v-if="field.required" aria-hidden="true" class="req">*</span>
    </legend>
    <div class="options">
      <label v-for="opt in field.options" :key="opt.value" class="option">
        <input
          :name="field.id"
          type="radio"
          :value="opt.value"
          :checked="modelValue === opt.value"
          :required="field.required"
          @change="emit('update:modelValue', opt.value)"
        />
        <span>{{ opt.label[locale] }}</span>
      </label>
    </div>
    <p v-if="error" :id="errorId" class="field-error" role="alert">{{ error }}</p>
  </fieldset>
</template>

<style scoped>
.field {
  border: 0;
  padding: 0;
  margin: 0 0 var(--space-5);
}
.options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-5);
  margin-top: var(--space-1);
}
.option {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  padding: var(--space-2) 0;
}
input[type='radio'] {
  accent-color: var(--color-text);
  width: 1rem;
  height: 1rem;
}
.option:hover span {
  color: var(--color-brand-dark);
}
</style>
